import React, { useState } from "react"

import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"
import Table from "~tailwindui/Table/Table"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"
import titlecase from "src/titlecase"

import LocalTime from "components/LocalTime"
import TimeProtectedLink from "components/TimeProtectedLink"
import TimeProtectedMiroLink from "components/TimeProtectedMiroLink"
import TutorCloudroomButton from "components/Tutor/components/Sessions/TutorCloudroomButton"

export type FutureSessionsProps = {
  studyGroupId: string
}

const FutureSessions: React.FC<FutureSessionsProps> = ({ studyGroupId }) => {
  const history = useHistory()
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query,
    variables: { studyGroupId, page },
  })

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { viewer } }) => (
        <Table
          title="Future Sessions"
          data={viewer.futureGroupSessions}
          rowCallback={({ id }) => {
            history.push(path("sessionDetails", { id }))
          }}
          changePage={setPage}
          cols={[
            {
              header: "Date",
              value: session => (
                <span key={session.id} className="flex items-center">
                  <div key={session.id} className="min-w-[80px]">
                    <LocalTime
                      format="ddd MMM D"
                      timestamp={session.startsAt}
                    />
                    <br />
                    <LocalTime omitDate timestamp={session.startsAt} />
                    &mdash;
                    <LocalTime omitDate timestamp={session.endsAt} />
                  </div>
                </span>
              ),
            },
            { header: "Subject", value: session => session.subject.name },
            {
              header: "Students",
              value: session => (
                <ul key={session.id}>
                  {session.students.map(student => (
                    <li className="flex items-center" key={student.id}>
                      <div>
                        <StyledLink
                          to={path("studentDetails", {
                            id: student.id,
                          })}
                        >
                          {student.fullName}
                        </StyledLink>
                      </div>
                    </li>
                  ))}
                </ul>
              ),
            },
            {
              header: "State",
              value: session => (
                <span key={session.id}>
                  {titlecase(session.statusState)} |{" "}
                  {titlecase(session.billState)}
                </span>
              ),
            },
            {
              header: "Actions",
              value: session => (
                <div className="flex flex-col gap-2 sm:flex-row">
                  {session.cloudroomActive && (
                    <TutorCloudroomButton
                      startsAt={session.startsAt}
                      id={session.id}
                    />
                  )}
                  {session.realtimeboards.length > 0 && (
                    <TimeProtectedMiroLink
                      tailwind
                      startsAt={session.startsAt}
                      email={viewer.ssoEmail}
                      boardId={session.realtimeboards[0].id}
                      boardPending={session.realtimeboardPending}
                      buttonClassName={buttonClassNames({
                        size: Size.Small,
                        color: Color.Green,
                      })}
                    />
                  )}
                  {session.googledocUrl && (
                    <TimeProtectedLink
                      tailwind
                      text="Google Doc"
                      href={session.googledocUrl}
                      startsAt={session.startsAt}
                      className={classNames(
                        buttonClassNames({
                          color: Color.Blue,
                          size: Size.Small,
                        })
                      )}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </WithLoadingIndicator>
  )
}

const query = gql`
  query StudyGroupFutureSessions($studyGroupId: ID!, $page: Int) {
    viewer {
      futureGroupSessions: sessions(
        filters: { future: true, studyGroupIds: [$studyGroupId] }
        page: $page
      ) {
        currentPage
        totalPages
        data {
          id
          startsAt
          endsAt
          cloudroomActive
          statusState
          billState
          mayCancelByTutor
          googledocUrl
          realtimeboardPending
          realtimeboards {
            id
            name
          }
          subject {
            id
            name
          }
          students {
            id
            fullName
          }
        }
      }
    }
  }
`

export default FutureSessions
